import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus, MagnifyingGlass, Truck, FilePdf,
  Trash, PencilSimple, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import YearMonthNav from '../components/YearMonthNav';

import { API } from '../config/api';

const statusColors = {
  emesso: 'bg-blue-100 text-blue-700 border-blue-200',
  in_transito: 'bg-amber-100 text-amber-700 border-amber-200',
  consegnato: 'bg-green-100 text-green-700 border-green-200',
  fatturato: 'bg-purple-100 text-purple-700 border-purple-200',
};

export default function DDTPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ddtList, setDdtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchDDT = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ddt`);
      setDdtList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching DDT:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDDT(); }, [fetchDDT]);

  const handleDelete = async (ddtId) => {
    if (!window.confirm(t('ddt.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/ddt/${ddtId}`);
      toast.success(t('ddt.deleted'));
      fetchDDT();
    } catch (err) {
      toast.error(t('ddt.createError'));
    }
  };

  const handleDownloadPdf = async (ddt) => {
    try {
      const response = await axios.get(`${API}/ddt/${ddt.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${ddt.ddt_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(t('ddt.pdfError'));
    }
  };

  const filtered = (ddtList || []).filter(d => {
    const matchSearch = d.ddt_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.client_name?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    const dateStr = d.date || '';
    if (dateStr.length < 7) return false;
    const year = parseInt(dateStr.substring(0, 4));
    if (year !== selectedYear) return false;
    if (selectedMonth !== null) {
      const month = parseInt(dateStr.substring(5, 7)) - 1;
      if (month !== selectedMonth) return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="ddt-title">
            <Truck size={28} weight="duotone" className="text-[#0033A0]" />
            {t('ddt.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">{t('ddt.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/ddt/new')}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white shrink-0"
          data-testid="new-ddt-button"
        >
          <Plus size={20} className="mr-2" />
          {t('ddt.newDdt')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
        <Input
          placeholder={t('ddt.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-[#E2E8F0]"
          data-testid="ddt-search"
        />
      </div>

      <YearMonthNav
        items={ddtList}
        dateField="date"
        totalField={null}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />

      {/* DDT List */}
      {loading ? (
        <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="text-center py-16">
            <Truck size={48} className="mx-auto text-[#CBD5E0] mb-4" weight="duotone" />
            <h3 className="font-medium text-[#0A0F1A] mb-2">{t('ddt.noDdt')}</h3>
            <p className="text-sm text-[#4A5568] mb-4">{t('ddt.createFirst')}</p>
            <Button
              onClick={() => navigate('/ddt/new')}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="create-first-ddt"
            >
              <Plus size={20} className="mr-2" />
              {t('ddt.newDdt')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((ddt) => (
            <Card key={ddt.id} className="border-[#E2E8F0] hover:border-[#CBD5E0] transition-colors" data-testid={`ddt-card-${ddt.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-outfit font-bold text-[#0033A0]">{ddt.ddt_number}</span>
                      <Badge className={`text-[10px] ${statusColors[ddt.status] || 'bg-gray-100 text-gray-600'}`}>
                        {t(`ddt.statusLabels.${ddt.status}`) || ddt.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#0A0F1A] font-medium">{ddt.client_name}</p>
                    <p className="text-xs text-[#4A5568]">
                      {formatDate(ddt.date)} | {(ddt.items || []).length} {t('ddt.items')} | {ddt.causal}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => handleDownloadPdf(ddt)}
                      title={t('ddt.downloadPdf')}
                      data-testid={`ddt-pdf-${ddt.id}`}
                    >
                      <FilePdf size={18} className="text-red-500" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => navigate(`/ddt/${ddt.id}/edit`)}
                      title={t('common.edit')}
                      data-testid={`ddt-edit-${ddt.id}`}
                    >
                      <PencilSimple size={18} className="text-[#4A5568]" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => handleDelete(ddt.id)}
                      title={t('common.delete')}
                      data-testid={`ddt-delete-${ddt.id}`}
                    >
                      <Trash size={18} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

